import React, { FC, useState, useMemo } from 'react'
import { observer } from 'mobx-react'
import { IonFooter } from '@ionic/react'
import { cartMobx, discountMobx, orderMobx, queueMobx, restaurantCenter, restaurantConfig, storeMobx } from 'restaurant'
import { checkoutMobx, timePickMobx, payPlanMobx, deliveryMobx, queueOrderingMobx, eatTypeStatusMobx, orderLiveMobx, payRechargeMobx, thirdPartyMobx, memberMobx } from '@mobxStore'
import { loadingLayer, modalLayer, toastLayer, getHistoryNew, showMsg, confirmLayer, formatTimeByToday, getPlatform } from '@utils'
import { format, ddayjs, getParams, delayTime, useDebounce, deepClone } from '@dt/fe-utils'
import { saveOrder, payOrder, payOrderReset } from '@common/utils/pay'
import { cloneDeep, sortBy, uniqBy } from '@dt/fe-utils/dist/lodash'
import { CashierController, DeliveryController } from '@ctrls/index'
import { thirdConfigG } from 'controllers/third'
import { ConfirmModal, Icon, SelectAddressModal } from '@cps'
import { SoldWarnModal } from '@pages/checkout/modal'
import { SettlementCard, PayAndRecharge, PayOther, RegisterGuide } from '@pages/checkout/component'
import { TEEatType } from 'restaurant/mobx/store.mobx'
import { DineinWaiter, OrderChangedBar, PickupWaiter } from './components'
import './index.scss'

const CartFooter: FC = () => {
  const history = getHistoryNew()
  const { isWeixinMiniProgram } = getPlatform()
  const { cartResult, ordersId, info } = cartMobx
  const { requiredProductResult } = cartMobx
  const { viewAddresses } = deliveryMobx
  const isCanCheckout = restaurantConfig.codeType === 'brand' ? (!!(timePickMobx.canPick && storeMobx.info.isMobileBookingOrder) || storeMobx.info.inSale) : storeMobx.info.inSale
  // 已有订单无新增餐品时只支付原订单，与购物车核心的必选豁免保持一致。
  const shouldCheckRequiredProduct = !ordersId || !!cartResult.presentTotalCnt
  const isRequiredProductReady = !shouldCheckRequiredProduct || requiredProductResult.isReady
  const isCanOrder = cartMobx.cartResult.totalCnt && cartResult.isCanSave && isRequiredProductReady && (((storeMobx.info.isMobileBookingOrder || timePickMobx.timePickList.length) && (cartMobx.info.startTime || (cartMobx.info.startTime === '' && storeMobx.info.inSale))) || storeMobx.info.inSale)
  const isCanDelivery = (cartMobx.info.eatType === 'delivery' && cartMobx.cartResult.itemActualPrice >= deliveryMobx.leastFee) || cartMobx.info.eatType !== 'delivery'
  const isCanSingleSale = cartMobx.canSingleSale
  const _thirdConfigG = thirdConfigG()
  const [isQueuePre, setIsQueuePre] = useState(false)

  // 品牌码且堂食且后付费且送餐制不允许直接下单
  const isBrandDineinDelivery = useMemo(() => Config.codeType === 'brand' && cartMobx.info.eatType === TEEatType.dinein && storeMobx.storeSceneMap?.[storeMobx.info.storeId + '']?.[TEEatType.dinein]?.payMode === 1 && storeMobx.storeSceneConfig?.diningMode === 2, [cartMobx.info.eatType, storeMobx.storeSceneConfig.diningMode])

  /** 是否可以下单 */
  const isCanAddCart = useMemo(() => {
    return CashierController.checkCanCart()
  }, [eatTypeStatusMobx.hasEnableEatType, storeMobx.info, timePickMobx.timePickList])

  // 其它支付方式
  const isCanPayOther = (!info.payMode && !(!isCanCheckout || !isCanDelivery || !isCanSingleSale || !isCanAddCart || !isRequiredProductReady)) || (info.payMode && !(isCanOrder || (!isCanOrder && !ordersId)) && (storeMobx.storeSceneConfig?.openAfterPayCustomerPay || !storeMobx.storeSceneConfig?.payMode))

  /** 继续加餐 */
  const handleBack = () => {
    if (history.getPrevState() !== '/menu') {
      history.replace('/menu')
    } else {
      history.goBack()
    }
  }

  // 处理售罄商品
  const handleProductConfirm = (isPayOther: boolean = false) => {
    if (cartMobx.viewDisabledCartItems.length) {
      modalLayer(SoldWarnModal, { errData: cartMobx.viewDisabledCartItems }).then(() => {
        cartMobx.clearDisabledItems()
        isPayOther ? handleOtherPayAction() : handlePayAction()
      }).catch(() => {
        cartMobx.clearDisabledItems()
        history.replace('/menu')
      })
    } else {
      isPayOther ? handleOtherPayAction() : handlePayAction()
    }
  }

  // 处理切换门店
  const handleSwitchStore = () => {
    history.push('/store', null, { tt: 'checkout' })
  }

  // 处理打包
  const handlePackup = (isConfirm: boolean) => {
    if (isConfirm) {
      cartMobx.confirmCartItemPack()
    } else {
      cartMobx.setNeedPack(false)
    }
  }

  // 排队预点餐处理
  const handleQueuePreCart = () => {
    const { tableCode } = queueMobx.queueInfo
    restaurantCenter.setConfig({
      codeType: 'table',
      tableCode: tableCode
    })
    cartMobx.setTableCode(tableCode)
  }

  // 排队预点餐扫码
  const handleQueuePreScan = () => {
    return new Promise((resolve, reject) => {
      _thirdConfigG?.scan(str => {
        if (str) {
          const { ct, b, s, t } = getParams({ url: str })
          if (ct === 'table' && Number(b) === Config.BRANDID && Number(s) === Config.STOREID && Number(t) === Number(queueMobx.queueInfo?.tableCode)) {
            resolve(true)
          } else {
            resolve(false)
          }
        } else {
          resolve(false)
        }
      })
    })
  }

  // 排队预点餐入桌
  const handleQueuePreSave = async () => {
    if (isQueuePre) {
      return true
    }
    if (!queueOrderingMobx.inPreQueueOrdering) {
      setIsQueuePre(true)
      return true
    }
    const isScan = await handleQueuePreScan()
    if (!isScan) {
      showMsg(i18n.t('queue.scanCodeTip'))
      return false
    }
    const checkinInfo = await queueOrderingMobx.checkinCallByBuyer().catch((err: any) => {
      showMsg(err.message)
      return err
    })
    if (isScan && !checkinInfo?.error) {
      handleQueuePreCart()
      setIsQueuePre(true)
      return true
    }
  }

  // 处理支付动作
  const handlePayAction = async () => {
    const isPre = await handleQueuePreSave()
    if (!isPre) return
    modalLayer.closeAll()
    // 直接支付(支付金额0、会员余额能全额支付)
    // const noWorryPay: boolean = authConfigMobx.isNoWorry && memberCardCanFullpay
    if ((payPlanMobx.totalAmount || (!payPlanMobx.totalAmount && payRechargeMobx.rechargeInfo)) && !payRechargeMobx.memberCardCanFullpay) {
      if (payRechargeMobx.rechargeInfo) {
        modalLayer(PayAndRecharge, null, 'payAndRechargeModal')
          .then(() => { })
          .catch(() => {
            payPlanMobx.beginCalc()
          })
      } else {
        modalLayer(SettlementCard, null, 'settlementCardModal')
          .then(() => { })
          .catch(async () => {
            // if (payRechargeMobx.memberCardDiscountOnlyBalancePay) {
            // }
            await discountMobx.deleteOnlyBalancePay()
            await delayTime(500)
            await discountMobx.setOnlyBalancePay(true)
          })
      }
    } else {
      payOrder({
        history,
        handlePayAction,
        handleSaveError: () => {
          modalLayer(ConfirmModal, {
            title: i18n.t('common.attention'),
            confirmText: i18n.t('cart.refreshMenu'),
            children: (<span>{i18n.t('cart.errorProduct')}</span>),
            className: 'cart-confirm-modal'
          }, 'confirmModal').then(() => {
            loadingLayer.show()
            restaurantCenter.initAll()
            setTimeout(() => loadingLayer.hide(), 2000)
          }).catch(() => {})
        },
        handlePayError: (forceOrder) => {
          modalLayer.closeAll()
          modalLayer(ConfirmModal, {
            title: i18n.t('common.attention'),
            confirmText: i18n.t('checkout.confirmRefresh'),
            children: (<span>{i18n.t('checkout.payOrderHasError')}</span>),
            className: 'cart-confirm-modal',
            backdropDismiss: false
          }, 'confirmModal').then(async () => {
            modalLayer.closeAll()
            loadingLayer.show()
            await payOrderReset(forceOrder)
            loadingLayer.hide()
          }).catch(() => {})
        }
      })
    }
  }

  const handleOtherPayAction = async () => {
    const isPre = await handleQueuePreSave()
    if (!isPre) return
    // if (payRechargeMobx.memberCardDiscountOnlyBalancePay) {
    // }
    await discountMobx.setOnlyBalancePay(false)

    await payPlanMobx.setMemberCardPayAmount(0)
    modalLayer.closeAll()
    modalLayer(SettlementCard, null, 'settlementCardModal')
      .then(() => {})
      .catch(async () => {
        // if (payRechargeMobx.memberCardDiscountOnlyBalancePay) {
        // }
        await discountMobx.deleteOnlyBalancePay()
        await delayTime(500)
        await discountMobx.setOnlyBalancePay(true)
        await payPlanMobx.setMemberCardPayAmount(payPlanMobx.totalAmountOffThirdCouponPrice)
      })
    if (payRechargeMobx.memberCardDiscountOnlyBalancePay && !thirdPartyMobx.couponResult?.offPrice) {
      setTimeout(() => toastLayer.info(i18n.t('pay.notMemberCardFullPayTip')), 500)
    }
  }

  // 处理保存订单
  const handleSaveOrder = useDebounce(async () => {
    if (!isRequiredProductReady) {
      return
    }
    if (!isCanSingleSale) {
      return
    }
    if (!checkoutMobx.isContactInfoFill()) {
      return
    }
    const isPre = await handleQueuePreSave()
    if (!isPre) return

    console.log('saveOrder =>>', '下单验证排队时长 handleSaveOrder')
    /** 立即时间是否偏差 */
    const isAspaTimeExpire = await timePickMobx.checkWaitingTimeToAspaTime()
    if (isAspaTimeExpire) {
      modalLayer.closeAll()
      return
    }
    saveOrder({
      handlePayAction,
      handleSaveError: () => {
        modalLayer(ConfirmModal, {
          title: i18n.t('common.attention'),
          confirmText: i18n.t('cart.refreshMenu'),
          children: (<span>{i18n.t('cart.errorProduct')}</span>),
          className: 'cart-confirm-modal'
        }, 'confirmModal').then(() => {
          loadingLayer.show()
          restaurantCenter.initAll()
          setTimeout(() => loadingLayer.hide(), 2000)
        }).catch(() => {})
      }
    })
  }, 500, [isRequiredProductReady, isCanSingleSale, isQueuePre])

  // 下单按钮文案
  const _orderTextRender = () => {
    return (queueOrderingMobx.inPreQueueOrdering || isBrandDineinDelivery) ? i18n.t('checkout.queueScanToOrder') : i18n.t('pay.submitOrder')
  }

  // 支付按钮文案
  const _payTextRender = () => {
    const _i18n = info.payMode ? 'pay.dineinOverPay' : payRechargeMobx.memberCardCanFullpay ? 'pay.balancePay' : payRechargeMobx.rechargeInfo ? 'pay.recharge' : isWeixinMiniProgram ? 'menu.go_pay' : 'pay.pay'
    const _amount = payRechargeMobx.rechargeInfo ? payRechargeMobx.rechargeInfo.money : payPlanMobx.totalAmount - thirdPartyMobx.couponResult.offPrice
    return (queueOrderingMobx.inPreQueueOrdering || isBrandDineinDelivery) ? i18n.t('checkout.queueScanToPay') : (
      <>
        {i18n.t(_i18n)} <big dangerouslySetInnerHTML={{ __html: format.toPriceLittleCurrency(_amount) }}></big>
        {payRechargeMobx.rechargeInfo && <div className='tip'>{i18n.t('pay.afterRechargeBalance', { amount: format.toPrice(payRechargeMobx.memberCardBalanceAfterRecharge) })}</div>}
      </>
    )
  }

  // 支付按钮点击
  const _payButtonClick = async (isPayOther: boolean = false) => {
    if (!isCanAddCart) {
      return
    }
    if (!isRequiredProductReady) {
      return
    }
    // 商家配置点餐必须登录手机号
    if (isWeixinMiniProgram && checkoutMobx.guideRegister && !memberMobx?.memberInfo?.mobile) {
      modalLayer(RegisterGuide)
      return
    }
    // 判断联系信息是否填写
    if (!checkoutMobx.isContactInfoFill()) {
      if (cartMobx.info.eatType === 'delivery') {
        if (viewAddresses?.length) {
          modalLayer(SelectAddressModal, {
            eatType: TEEatType.delivery,
          }, 'SelectAddressModal').then(() => {
          }, () => { })
        } else {
          DeliveryController.editAddressModal({
            eatType: TEEatType.delivery,
            callback (store) {
            }
          })
        }
      }
      return
    }
    // 判断外卖起送金额
    if (!isCanDelivery) {
      return
    }
    // 不能单点
    if (!isCanSingleSale) {
      return
    }
    // 预约时间
    if (cartMobx.info.startTime) {
      // 如果开启预约时间，判断当前时间是否大于3分钟
      const makeTime = storeMobx.info?.preparationTime?.[0]?.time?.data === 0 ? 0 : (storeMobx.info?.preparationTime?.[0]?.time?.data || 0)
      if (
        ddayjs()
          .subtract(3 + makeTime, 'minute')
          .format() >= cartMobx.info.startTime
      ) {
        showMsg(i18n.t('cart.timePickDsiabled'))
        timePickMobx.reset()
        return
      }
    }
    // 当前不可下单的状态
    if (!isCanOrder && !info.payMode) {
      if (!cartMobx.cartResult.totalCnt || !cartResult.isCanSave) {
        showMsg(i18n.t('cart.orderTipsProduct'))
        return
      } else if (!(((storeMobx.info.isMobileBookingOrder || timePickMobx.timePickList.length) && (cartMobx.info.startTime || (cartMobx.info.startTime === '' && storeMobx.info.inSale))) || storeMobx.info.inSale)) {
        showMsg(i18n.t('cart.orderTipsStore'))
        return
      }
      showMsg(i18n.t('cart.orderTips'))
      return
    }

    console.log('saveOrder =>>', '下单验证排队时长 _payButtonClick')
    /** 立即时间是否偏差 */
    const isAspaTimeExpire = await timePickMobx.checkWaitingTimeToAspaTime()
    if (isAspaTimeExpire) {
      modalLayer.closeAll()
      return
    }
    if (restaurantConfig.codeType === 'brand') {
      let _times = []
      const _asapTime = timePickMobx.asapTime.time ? `${ddayjs(timePickMobx.asapTime.time).format('YYYY-MM-DD')} ${timePickMobx.asapTime.timeStr}` : ''
      const _getTimeText = (startTime: string) => {
        const index = Number(ddayjs(startTime).format('YYYYMMDD')) - Number(ddayjs().format('YYYYMMDD'))
        const date = index >= 1 ? `${index === 1 ? i18n.t('checkout.tomorrow') : format.toDate(startTime)}` : ''
        return `${date} ${format.toTime(ddayjs(startTime, 'client').valueOf())}`
      }
      const _list = uniqBy(cloneDeep(cartMobx.viewCartItems).map(item => {
        const startTime = item.startTime || cartMobx.info.startTime || _asapTime
        return { startTime }
      }).filter(i => !!i.startTime), 'startTime')
      if (_list?.filter(i => i.startTime !== _asapTime)?.length || cartMobx.info.eatType === 'delivery') {
        _times = sortBy(_list, i => ddayjs(i.startTime).valueOf()).map(i => _getTimeText(i.startTime))
      }
      modalLayer(
        ConfirmModal,
        {
          className: `brand-orders-modal ${_times?.length ? '' : 'no-time-list'}`,
          confirmText: i18n.t('common.ok'),
          cancelText: i18n.t('common.cancel'),
          hasFooter: false,
          canClose: true,
          children: (
            cartMobx.info.eatType === 'delivery' ? (
              <div className='brand-order-detail'>
                <div className='title'>
                  <Icon name='delivery-title' />
                  {i18n.t('menu.delivery')}
                </div>
                <div className='address'>
                  <Icon name='location' />
                  {cartMobx.info?.contactInfo?.shortAddress}
                </div>
                <div className='detail'>
                  {cartMobx.info.contactName} {cartMobx.info.contactMobile}
                </div>
                {
                  _times?.length ? (
                    <div className='time-list'>
                      <div className='time-title'>
                        <Icon size={20} name='time2x' />
                        {i18n.t(`checkout.${cartMobx.info.eatType}${_times?.length > 1 ? 'OrderTimes' : 'OrderTime'}`)}
                      </div>
                      {_times?.map(item => <div key={item} className='time-item'>{item}</div>)}
                    </div>
                  ) : null
                }
                <div className='btn-box'>
                  <div className='btn-submit' data-cypid='brandOrdersModalConfirm' onClick={() => handleProductConfirm(isPayOther)}>{i18n.t('common.ok')}</div>
                </div>
              </div>
            ) : (
              <div className='brand-order-detail'>
                <div className='title'>
                  <Icon name='delivery-title' />
                  {i18n.t(`menu.${cartMobx.info.eatType}`)}
                </div>
                <div className='address'>
                  <Icon name='location' />
                  {storeMobx.info.storeName}
                </div>
                <div className='detail'>
                  {storeMobx.info.address}
                </div>
                <div className='store-tag'>
                  {
                    storeMobx.info.boughtCnt ? (
                      <span className='store-bought-cnt'><span className='txt'>{i18n.t('store.boughtStoreByStore', { num: storeMobx.info.boughtCnt })}</span></span>
                    ) : <></>
                  }
                  {
                    !!storeMobx.info.distanceWithUnit && (
                      <span className='store-distance'>
                        {i18n.t('menu.distance', { distance: storeMobx.info.distanceWithUnit })}
                      </span>
                    )
                  }
                </div>
                {
                  _times?.length ? (
                    <div className='time-list'>
                      <div className='time-title'>
                        <Icon size={20} name='time2x' />
                        {i18n.t(`checkout.${cartMobx.info.eatType}${_times?.length > 1 ? 'OrderTimes' : 'OrderTime'}`)}
                      </div>
                      {_times?.map(item => <div key={item} className='time-item'>{item}</div>)}
                    </div>
                  ) : null
                }
                <div className='btn-box'>
                  <div className='btn-submit' data-cypid='brandOrdersModalConfirm' onClick={() => handleProductConfirm(isPayOther)}>{i18n.t('common.ok')}</div>
                  {storeMobx.viewStoreList?.length > 1 && <div className='btn-switch' data-cypid='brandOrdersModalSwitchStore' onClick={() => handleSwitchStore()}>{i18n.t('newCart.switchStore')}</div>}
                </div>
              </div>
            )
          ),
        },
        'confirmModal'
      ).then(() => {
        handleProductConfirm(isPayOther)
      })
    } else {
      if (info.payMode) {
        isPayOther ? handleOtherPayAction() : handlePayAction()
      } else {
        handleProductConfirm(isPayOther)
      }
    }
  }

  // 支付按钮渲染
  const _payButtonRender = () => {
    return (
      <button
        className={`wide-btn${payRechargeMobx.rechargeInfo && isCanDelivery && isCanSingleSale && isRequiredProductReady ? ' has-tip' : ''}${!isCanCheckout || !isCanDelivery || !isCanSingleSale || !isCanAddCart || !isRequiredProductReady ? ' disabled' : ''}`}
        data-cypid='cartFooterPayButton'
        onClick={() => {
          _payButtonClick()
        }}
      >
        {
          !isCanCheckout ? (
            <div>{i18n.t('store.restingStore')}</div>
          ) : (
            !isCanAddCart ? (
              <div>
                <p className='pause-order-tip1'>{i18n.t('store.pauseOrdering1')}</p>
                <p className='pause-order-tip2'>{i18n.t('store.openOrdering1', { time: formatTimeByToday(timePickMobx.pauseOrderTime?.endTime) })}</p>
              </div>
            ) : (
              !isRequiredProductReady ? (
                <div>{i18n.t('cart.requiredProductAction')}</div>
              ) : isCanDelivery ? (
                isCanSingleSale ? (
                  <>
                    {_payTextRender()}
                  </>
                ) : (
                  <>
                    {i18n.t('menu.canNotSingleSale')}
                  </>
                )
              ) : (
                <>
                  {
                    cartMobx.cartResult.itemActualPrice > 0 ? (
                      <>
                        <big dangerouslySetInnerHTML={{ __html: i18n.t('pay.diffDelivery', { price: format.toPriceLittleCurrency(deliveryMobx.leastFee - cartMobx.cartResult.itemActualPrice) }) }}></big>
                      </>
                    ) : (
                      <>
                        <big dangerouslySetInnerHTML={{ __html: format.toPriceLittleCurrency(deliveryMobx.leastFee) }}></big> {i18n.t('pay.leastDelivery')}
                      </>
                    )
                  }
                </>
              )
            )
          )
        }
      </button>
    )
  }
  // 提交订单或结算操作
  const _orderFooterRender = () => {
    return (
      <>
        <button className='default-btn' data-cypid='cartFooterOrderButtonBack' onClick={handleBack}>
          {(info.payMode && !storeMobx.info.inSale) || (!info.payMode && !isCanCheckout) ? i18n.t('common.back') : i18n.t('cart.addMoreItems')}
        </button>
        {info.payMode ? (
          (isCanOrder || (!isCanOrder && !ordersId)) ? (
            <button
              data-cypid='cartFooterOrderButtonSave'
              className={`wide-btn${!isCanOrder || !isCanAddCart || !isCanSingleSale || !isRequiredProductReady ? ' disabled' : ''}`}
              onClick={() => handleSaveOrder()}
            >
              {
                storeMobx.info.inSale ? (
                  !isRequiredProductReady ? i18n.t('cart.requiredProductAction') : isCanSingleSale ? _orderTextRender() : i18n.t('menu.canNotSingleSale')
                ) : i18n.t('store.restingStore')
              }
            </button>
          ) : (
            (storeMobx.storeSceneConfig?.openAfterPayCustomerPay || !storeMobx.storeSceneConfig?.payMode) ? (
              _payButtonRender()
            ) : (
              storeMobx.info.customerCheckoutWay === 0 ? (
                <>
                  <button
                    className='wide-btn'
                    style={{ display: 'block', backgroundColor: '$themeColor' }}
                  >
                    <small>{cartMobx.info.eatType === 'pickup' ? i18n.t('newCart.contactGoShopPay') : i18n.t('common.contactAttendantBill')}</small>
                    <br />
                    <span>{format.toPrice(payPlanMobx.totalAmount - thirdPartyMobx.couponResult.offPrice)}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className='wide-btn'
                    style={{ display: 'block', backgroundColor: '$themeColor' }}
                  >
                    <small>{cartMobx.info.eatType === 'pickup' ? i18n.t('newCart.contactGoShopPay') : i18n.t('common.moveCashierDesk')}</small><br />
                    <span>{format.toPrice(payPlanMobx.totalAmount - thirdPartyMobx.couponResult.offPrice)}</span>
                  </button>
                </>
              )
            )
          )
        ) : (
          _payButtonRender()
        )}
      </>
    )
  }

  // 扫码入桌点单提示
  const _scanToOrderTipFooter = () => {
    return (
      <div className='cart-waiter-footer'>
        <div className='cwf-wrapper'>
          <div className='cwf-info'>
            <i className='dicon dicon-entry-newscancode0'></i>
            <div>
              <h3>{i18n.t('checkout.preOrderSubmitScanCode')}</h3>
              <small>{i18n.t('checkout.preOrderSubmitContactWaiter')}</small>
            </div>
          </div>
          <div className='cwf-btn' data-cypid='cartFooterQueueBack' onClick={() => history.goBack()}>{i18n.t('checkout.reChooseProduct')}</div>
        </div>
      </div>
    )
  }

  // 提交订单或结算操作(排队预点餐)
  const _queueFooterRender = () => {
    return (
      <>
        {
          queueMobx.queueInfo?.status === 'Waiting' ? (
            <div className='cart-waiter-footer'>
              <div className='cwf-wrapper'>
                <div className='cwf-info'>
                  <i className='iconfont icon-sandglass'></i>
                  <div>
                    {storeMobx.info?.preOrderCustomTips ? <h3>{storeMobx.info?.preOrderCustomTips}</h3> : <h3>{i18n.t('checkout.preOrderSubmitWaiting')}</h3>}
                  </div>
                </div>
                <div className='cwf-btn' data-cypid='cartFooterQueueBack' onClick={() => history.goBack()}>{i18n.t('checkout.reChooseProduct')}</div>
              </div>
            </div>
          ) : !_thirdConfigG?.isInited() ? _scanToOrderTipFooter() : (
            <div className='cart-button-footer'>
              {_orderFooterRender()}
            </div>
          )
        }
      </>
    )
  }

  // 打包操作
  const _packFooterRender = () => {
    return (
      <>
        <button className='default-btn' data-cypid='cartFooterPackBack' onClick={() => handlePackup(false)}>
          {i18n.t('mkt.common.cancel')}
        </button>
        <button data-cypid='cartFooterPackConfirm' onClick={() => handlePackup(true)}>
          {i18n.t('mkt.common.confirm')}
        </button>
      </>
    )
  }

  return (
    <IonFooter className='cart-footer'>
      {
        orderMobx.viewOrder.isWait ? (
          /** 等待服务员下单 */
          orderMobx.viewOrder.eatType === TEEatType.pickup ? (
            <PickupWaiter />
          ) : (
            <DineinWaiter />
          )
        ) : (
          <>
            {/* 后付费未接单，但是修改餐品 */}
            <OrderChangedBar />
            {
              queueOrderingMobx.inPreQueueOrdering ? (
                <>
                  {cartMobx.needPack ? <div className='cart-button-footer'>{_packFooterRender()}</div> : _queueFooterRender()}
                </>
              ) : isBrandDineinDelivery ? ( // 品牌码堂食送餐
                cartMobx.needPack ? <div className='cart-button-footer'>{_packFooterRender()}</div> : _scanToOrderTipFooter()
              ) : (
                <>
                  {
                    payRechargeMobx.memberCardCanFullpay && payPlanMobx.totalAmount > 0 && !!isCanPayOther && (
                      <PayOther
                        onClick={() => {
                          _payButtonClick(true)
                        }}
                      />
                    )
                  }
                  <div className='cart-button-footer'>
                    {cartMobx.needPack ? _packFooterRender() : _orderFooterRender()}
                  </div>
                </>
              )
            }
          </>
        )
      }
    </IonFooter>
  )
}

export default observer(CartFooter)
